import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { round } from 'lodash-es';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationModal, DialogActionButton, DropdownButton } from '../../../components/common';
import { LeafletMap, LoaderControl } from '../../../components/LeafletMap';
import { dictionary } from '../../../lang';
import { activatePolygon, clearPolygonOutcomes, createPolygon, deactivatePolygons, deletePolygons, getPolygonStatistics, getTeamPins, getTeamPolygons, reassignRepsToPolygons, unassignRepsFromPolygons, updatePolygonBoundaries, selectPolygonPreview, selectPolygonsStatistics, selectTeamCells, selectTeamPins, selectTeamPolygons, } from '../../../redux';
import { TeamMapContext } from '.';
import { prepareMapLegendData } from './helpers';
import MapLegend from './MapLegend';
import NotLicensedRepsModal from './NotLicensedRepsModal';
import PolygonDetailsPanel from './PolygonDetailsPanel';
import TeamBoundary from './TeamBoundary';
import TeamChoropleth from './TeamChloropleth';
import TeamMapControlsBar from './TeamMapControlsBar';
import TeamPolygons from './TeamPolygons';
/**
 * @param {number} teamId
 * @param {import('geojson').MultiPolygon | undefined} boundary
 * @param {import('geojson').Point | undefined} center
 * @param {Array<number>} repIds
 * @param {Array<{id: number; name: string}>} repOptions
 */
const TeamMap = ({ teamId, boundary, center, repIds, repOptions, }) => {
    const dispatch = useDispatch();
    const [layerChoice, setLayerChoice] = useState(null);
    const [selectedPolygon, setSelectedPolygon] = useState(null);
    const [multipleSelectedPolygons, setMultipleSelectedPolygons] = useState([]);
    const [polygons, setPolygons] = useState([]);
    const [visiblePolygons, setVisiblePolygons] = useState([]);
    const [panelStatistics, setPanelStatistics] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [loadedReps, setLoadedReps] = useState([]);
    const [chosenReps, setChosenReps] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false });
    const [restrictedRepsLicenseInfo, setRestrictedRepsLicenseInfo] = useState([]);
    const [canCloseTheModal, setCanCloseTheModal] = useState(true);
    const [viewBoundary, setViewBoundary] = useState(null);
    const { pinMode, heatMap, isPolygons, displayPins, isPinsScoreMode, setIsPolygons, displayPinsOutcomes, toggleDisplayPinsOutcomes, } = useContext(TeamMapContext);
    const { teamPolygons, isLoading: isTeamPolygonsLoading } = useSelector(selectTeamPolygons);
    const { polygonPreview, isLoading: isPolygonPreviewLoading } = useSelector(selectPolygonPreview);
    const { polygonsStatistics, isLoading: isPolygonsStatisticsLoading } = useSelector(selectPolygonsStatistics);
    const { isLoading: isTeamCellsLoading } = useSelector(selectTeamCells);
    const { isLoading: isTeamPinsLoading } = useSelector(selectTeamPins);
    useEffect(() => {
        if (displayPinsOutcomes && (multipleSelectedPolygons.length > 1 || !multipleSelectedPolygons.length)) {
            toggleDisplayPinsOutcomes();
        }
    }, [multipleSelectedPolygons, displayPinsOutcomes, toggleDisplayPinsOutcomes]);
    useEffect(() => {
        // TODO: Hotfix.
        //       This check is needed to prevent dispatching effect twice.
        //       Which is triggered by re-mounting TeamMap component depending on `team.isLoading` state.
        //       `team.isLoading` is `false` by default, and `TeamMap` component mounts.
        //       Then `isLoading` switch to true and team data loading starts - `TeamMap` - unmounts.
        //       Once team data is loaded, `isLoading` sets to `false` and `TeamMap` mounts again.
        //       Possible solution: move this call to the parent component.
        if (!teamPolygons.length && !isTeamPolygonsLoading) {
            dispatch(getTeamPolygons.request({
                teamId,
            }));
        }
    }, [
        dispatch,
        teamId,
        teamPolygons.length,
        isTeamPolygonsLoading,
    ]);
    useEffect(() => {
        const currentlyVisiblePolygons = repIds.length
            ? polygons.filter((polygon) => polygon.reps.some((rep) => repIds.includes(rep.rep_id))) : polygons;
        setVisiblePolygons(currentlyVisiblePolygons);
    }, [polygons, repIds]);
    useEffect(() => {
        if (!openDialog) {
            setSelectedPolygon(null);
            setMultipleSelectedPolygons([]);
            setLoadedReps([]);
        }
    }, [openDialog]);
    // TODO: Do we need this?
    //useEffect(() => {
    //  return () => {
    //    clearPreview();
    //    setOpenDialog(false);
    //  };
    //}, [clearPreview]);
    useEffect(() => {
        if (multipleSelectedPolygons.length) {
            const selectedStatistics = Object.entries(polygonsStatistics)
                .filter(([key]) => multipleSelectedPolygons.includes(Number(key)))
                .map(([, value]) => value);
            const initialStats = {
                percentage: 0,
                totalAddresses: 0,
                qualifiedAddresses: 0,
                totalReps: 0,
                repIds: [],
                lastKnocks: [],
            };
            if (selectedStatistics.length) {
                const generalStatistics = selectedStatistics.reduce((acc, item, _index, array) => {
                    const { totalAddresses, qualifiedAddresses, repIds } = item ?? {};
                    const uniqueRepsIds = [...new Set(acc.repIds.concat(repIds))];
                    const averagePercentage = array.reduce((acc, item) => acc + item.percentage, 0) / array.length;
                    acc.percentage = round(averagePercentage, 2);
                    acc.totalAddresses += totalAddresses;
                    acc.qualifiedAddresses += qualifiedAddresses;
                    acc.totalReps = uniqueRepsIds.length;
                    acc.repIds = uniqueRepsIds;
                    acc.lastKnocks = multipleSelectedPolygons.length !== 1 ? [] : item.lastKnocks;
                    return acc;
                }, initialStats);
                if (generalStatistics) { // TODO: it seems to be always true
                    setPanelStatistics(generalStatistics);
                    setLoadedReps(generalStatistics.repIds);
                }
            }
        }
    }, [polygonsStatistics, multipleSelectedPolygons]);
    useEffect(() => {
        if (polygonPreview) {
            setOpenDialog(true);
            setPanelStatistics(polygonPreview);
        }
    }, [polygonPreview]);
    useEffect(() => {
        setDialogLoading(// TODO: it should be a result of a complex selector
        isPolygonPreviewLoading // TODO: Not sure if it's needed: dialog opens only when polygonPreview is loaded.
            || isPolygonsStatisticsLoading);
    }, [
        isPolygonPreviewLoading,
        isPolygonsStatisticsLoading,
    ]);
    useEffect(() => {
        setIsPolygons(Boolean(polygons.length));
    }, [polygons, setIsPolygons]);
    useEffect(() => {
        setPolygons(teamPolygons);
    }, [teamPolygons]);
    const closeDialog = () => {
        setOpenDialog(false);
    };
    const openConfirmModal = (modal) => () => {
        setConfirmModal(modal);
    };
    const closeConfirmModal = () => {
        setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
    };
    const getRepIdList = () => {
        return chosenReps.map(({ id }) => id);
    };
    const handleAssign = () => {
        const idList = getRepIdList();
        setCanCloseTheModal(false);
        dispatch(reassignRepsToPolygons.request({
            teamId,
            polygonIds: multipleSelectedPolygons,
            repIds: idList,
            callback: (licensingInformationData, restrictedReps) => {
                const restrictedRepsLicensingInfo = [];
                for (const repId in licensingInformationData) {
                    if (!licensingInformationData[repId].has_access_to_work) {
                        restrictedRepsLicensingInfo.push(licensingInformationData[repId]);
                    }
                }
                const updatedPolygons = polygons.map((polygon) => {
                    const restrictedRepsForPolygon = restrictedReps[polygon.polygon_id];
                    if (restrictedRepsForPolygon) {
                        const updatedReps = chosenReps.filter((rep) => !restrictedRepsForPolygon.includes(rep.id));
                        return {
                            ...polygon,
                            reps: updatedReps,
                            reps_count: updatedReps.length,
                            active: true,
                        };
                    }
                    return polygon;
                });
                setRestrictedRepsLicenseInfo(restrictedRepsLicensingInfo);
                setPolygons(updatedPolygons);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const handleUnassign = () => {
        setCanCloseTheModal(false);
        dispatch(unassignRepsFromPolygons.request({
            polygonIds: multipleSelectedPolygons,
            callback: (unassignedPolygons) => {
                updatePolygonsOnMap({ reps: [], reps_count: 0 }, unassignedPolygons);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const handleActivatePolygon = () => {
        const idList = getRepIdList();
        setCanCloseTheModal(false);
        dispatch(activatePolygon.request({
            teamId,
            polygonId: selectedPolygon.polygon_id,
            repIds: idList,
            callback: (licensingInformationData, restrictedReps) => {
                const restrictedRepsLicensingInfo = [];
                for (const repId in licensingInformationData) {
                    if (!licensingInformationData[repId].has_access_to_work) {
                        restrictedRepsLicensingInfo.push(licensingInformationData[repId]);
                    }
                }
                let updatedReps = chosenReps;
                if (restrictedReps) {
                    const restrictedRepsForPolygon = restrictedReps[selectedPolygon.polygon_id];
                    updatedReps = restrictedReps
                        ? chosenReps.filter((rep) => !restrictedRepsForPolygon.includes(rep.id))
                        : chosenReps;
                }
                updatePolygonsOnMap({
                    active: true,
                    reps: updatedReps,
                    reps_count: updatedReps.length,
                });
                setRestrictedRepsLicenseInfo(restrictedRepsLicensingInfo);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const handleDeactivatePolygon = () => {
        setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
        setCanCloseTheModal(false);
        dispatch(deactivatePolygons.request({
            polygonIds: multipleSelectedPolygons,
            callback: (deactivatedPolygons) => {
                updatePolygonsOnMap({ reps: [], reps_count: 0, active: false }, deactivatedPolygons);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const handleClearOutcomesPolygon = () => {
        setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
        dispatch(clearPolygonOutcomes.request({
            polygonIds: multipleSelectedPolygons,
            callback: () => {
                if (pinMode) { // TODO: Move this to saga like we do for other similar cases
                    dispatch(getTeamPins.request({
                        teamId,
                        boundaries: viewBoundary,
                    }));
                }
                setOpenDialog(false);
            },
        }));
    };
    const handleDeletePolygon = () => {
        setCanCloseTheModal(false);
        setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
        dispatch(deletePolygons.request({
            polygonIds: multipleSelectedPolygons,
            callback: (deletedPolygons) => {
                const remainingPolygons = polygons
                    .filter((polygon) => !deletedPolygons.includes(polygon.polygon_id));
                setPolygons(remainingPolygons);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const handleUpdateBoundary = (updatedPolygon) => {
        setCanCloseTheModal(false);
        dispatch(updatePolygonBoundaries.request({
            polygonId: updatedPolygon.polygon_id,
            boundaries: updatedPolygon.boundary,
            callback: () => {
                const updatedPolygons = polygons.map((polygon) => (updatedPolygon.polygon_id === polygon.polygon_id ? updatedPolygon : polygon));
                setPolygons(updatedPolygons);
                setCanCloseTheModal(true);
            },
        }));
    };
    const updatePolygonsOnMap = (updatedFields, polygonsIds) => {
        const updatedPolygons = polygons.map((polygon) => {
            let isUpdated;
            if (polygonsIds) {
                isUpdated = polygonsIds.includes(polygon.polygon_id);
            }
            else {
                isUpdated = selectedPolygon?.polygon_id === polygon.polygon_id;
            }
            if (isUpdated) {
                return {
                    ...polygon,
                    ...updatedFields,
                };
            }
            else {
                return polygon;
            }
        });
        setPolygons(updatedPolygons);
    };
    const addPolygonToMap = (polygonToAdd) => {
        const newPolygon = {
            ...polygonToAdd,
            reps_count: polygonToAdd.reps.length,
        };
        const newPolygons = [...polygons];
        newPolygons.push(newPolygon);
        setPolygons(newPolygons);
    };
    const handlePolygonSelect = (selected) => {
        setDialogLoading(true);
        setOpenDialog(true);
        // When the choropleth is loaded, there is a delay for the user
        // when clicking polygons due to the getPolygonStatistics call.
        // This timeout sends the api call to the end of the render queue
        // avoid the delay
        setTimeout(() => {
            dispatch(getPolygonStatistics.request({
                polygonId: selected.polygon_id,
            }));
        }, 0);
    };
    const getMultiplePolygonStatistics = (polygonId) => {
        dispatch(getPolygonStatistics.request({
            polygonId,
        }));
    };
    const confirmCreatePolygon = () => {
        setCanCloseTheModal(false);
        dispatch(createPolygon.request({
            teamId,
            boundaries: selectedPolygon.geometry,
            repIds: getRepIdList(),
            callback: (polygon, licensingInformationData) => {
                const restrictedRepsLicensingInfo = [];
                for (const repId in licensingInformationData) {
                    if (!licensingInformationData[repId].has_access_to_work) {
                        restrictedRepsLicensingInfo.push(licensingInformationData[repId]);
                    }
                }
                setRestrictedRepsLicenseInfo(restrictedRepsLicensingInfo);
                addPolygonToMap(polygon);
                setOpenDialog(false);
                setCanCloseTheModal(true);
            },
        }));
    };
    const getMultipleSelectedPolygons = () => {
        const multiPolygons = [];
        polygons.forEach((polygon) => {
            if (multipleSelectedPolygons.includes(polygon.polygon_id)) {
                multiPolygons.push(polygon);
            }
        });
        return multiPolygons;
    };
    const getActionButton = () => {
        const polygonsCount = multipleSelectedPolygons.length;
        const singularOrPluralKey = polygonsCount > 1 ? 'PLURAL' : 'SINGULAR';
        if (dialogLoading || !selectedPolygon) {
            return null;
        }
        else if (selectedPolygon.isPreview) {
            return (_jsx(DialogActionButton, { className: 'bg-primary-300', triggerAction: confirmCreatePolygon, text: dictionary.CREATE_POLYGON, disabled: false }));
        }
        else {
            return (_jsx(DropdownButton, { options: [
                    {
                        key: 'updateReps',
                        label: dictionary.UPDATE_REPS,
                        onClick: handleAssign,
                    },
                    {
                        key: 'removeReps',
                        label: dictionary.REMOVE_ALL_REPS,
                        onClick: handleUnassign,
                        isHidden: getMultipleSelectedPolygons().every((polygon) => !polygon.reps_count),
                    },
                    {
                        key: 'clearOutcomes',
                        label: dictionary.CLEAR_OUTCOMES,
                        onClick: openConfirmModal({
                            isOpen: true,
                            title: dictionary.CLEAR_OUTCOMES_CONFIRMATION_TITLE[singularOrPluralKey],
                            onAction: handleClearOutcomesPolygon,
                            message: dictionary.CLEAR_OUTCOMES_CONFIRMATION_MESSAGE[singularOrPluralKey],
                            confirmLabel: dictionary.CLEAR_OUTCOMES_CONFIRMATION_CONFIRM,
                        }),
                    },
                    {
                        key: 'activate',
                        label: dictionary.ACTIVATE_POLYGON,
                        onClick: handleActivatePolygon,
                        isHidden: polygonsCount > 1 || getMultipleSelectedPolygons().every((polygon) => polygon.active),
                    },
                    {
                        key: 'deactivate',
                        label: dictionary.DEACTIVATE_POLYGON[singularOrPluralKey],
                        onClick: openConfirmModal({
                            isOpen: true,
                            title: dictionary.DEACTIVATE_POLYGON_CONFIRMATION_TITLE[singularOrPluralKey],
                            onAction: handleDeactivatePolygon,
                            message: dictionary.DEACTIVATE_POLYGON_CONFIRMATION_MESSAGE[singularOrPluralKey],
                            confirmLabel: dictionary.DEACTIVATE_POLYGON_CONFIRMATION_CONFIRM[singularOrPluralKey],
                        }),
                        isHidden: getMultipleSelectedPolygons().every((polygon) => !polygon.active),
                    },
                    {
                        key: 'delete',
                        label: dictionary.DELETE_POLYGON[singularOrPluralKey],
                        onClick: openConfirmModal({
                            isOpen: true,
                            title: dictionary.CLEAR_OUTCOMES_CONFIRMATION_TITLE[singularOrPluralKey],
                            onAction: handleDeletePolygon,
                            message: dictionary.DELETE_POLYGON_CONFIRMATION_MESSAGE[singularOrPluralKey],
                            confirmLabel: dictionary.DELETE_POLYGON_CONFIRMATION_CONFIRM[singularOrPluralKey],
                        }),
                    },
                ] }));
        }
    };
    return (_jsxs("div", { className: "relative", children: [pinMode && _jsx(TeamMapControlsBar, {}), _jsxs(LeafletMap, { center: center, zoom: 10, onLayerChange: setLayerChoice, children: [(isTeamCellsLoading || isTeamPinsLoading) && (_jsx(LoaderControl, {})), _jsx(TeamBoundary, { boundary: boundary, layerChoice: layerChoice }), _jsx(TeamChoropleth, { teamId: teamId, selectedPolygon: polygonsStatistics[selectedPolygon?.polygon_id], setViewBoundary: setViewBoundary }), _jsx(TeamPolygons, { teamId: teamId, polygons: visiblePolygons, selectedPolygon: selectedPolygon, setSelectedPolygon: setSelectedPolygon, onPolygonSelect: handlePolygonSelect, onPolygonEdit: handleUpdateBoundary, multipleSelection: multipleSelectedPolygons, selectMultiplePolygons: setMultipleSelectedPolygons, getMultiplePolygonStatistics: getMultiplePolygonStatistics, closeDialog: closeDialog }), _jsx(MapLegend, { data: prepareMapLegendData(heatMap, pinMode, isPinsScoreMode), pinMode: pinMode, isPolygons: isPolygons, displayPins: displayPins, displayPinsOutcomes: displayPinsOutcomes })] }), _jsx(PolygonDetailsPanel, { dialogTitle: dictionary.SELECTED_POLYGON_INFORMATION[multipleSelectedPolygons.length > 1 ? 'PLURAL' : 'SINGULAR'], loading: dialogLoading, actionButton: getActionButton(), open: openDialog, statistics: panelStatistics, setOpen: setOpenDialog, repOptions: repOptions, initialChosenReps: loadedReps, setChosenReps: setChosenReps, canHistoryBeOpened: selectedPolygon && !selectedPolygon.isPreview && multipleSelectedPolygons.length === 1, canOutcomesBeShown: pinMode && selectedPolygon && !selectedPolygon.isPreview && multipleSelectedPolygons.length === 1, canCloseTheModal: canCloseTheModal }), _jsx(ConfirmationModal, { isOpened: confirmModal.isOpen, modalWidth: "max-w-[528px] w-full", title: confirmModal?.title, onCancel: closeConfirmModal, onAction: confirmModal?.onAction, message: confirmModal?.message, confirmLabel: confirmModal?.confirmLabel }), _jsx(NotLicensedRepsModal, { restrictedRepsLicenseInfo: restrictedRepsLicenseInfo, setRestrictedRepsLicenseInfo: setRestrictedRepsLicenseInfo }), _jsx("div", { children: dictionary.SELECT_MULTIPLE_POLYGONS_TIP })] }));
};
TeamMap.propTypes = {
    teamId: PropTypes.number,
    boundary: PropTypes.shape({
        type: PropTypes.oneOf(['MultiPolygon']),
        coordinates: PropTypes.array,
    }),
    center: PropTypes.shape({
        type: PropTypes.oneOf(['Point']),
        coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    repIds: PropTypes.arrayOf(PropTypes.number),
    repOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    })),
};
export default TeamMap;
